// DEPRECATED (2026-07-19): pwa/src/screens/ is scheduled for deletion in STEP-M8-V4-LEGACY-SCREENS.
// Active screens are in pwa/src/v4/screens/ — use those instead.
import { useState } from "react";
import {
  getSessionHandoff,
  getOpsReport,
  getConsultationSummary,
  getDiagnosticReport,
  getDispatchMetrics,
  fetchFrictionFunnel,
  getWaveFunnel,
} from "../v4/lib/dispatch-queries.js";
import type {
  SessionHandoffResponse,
  OpsReportResponse,
  ConsultationSummaryResponse,
  DiagnosticReportResponse,
  DispatchMetricsResponse,
  FrictionFunnelMetrics,
  WaveFunnelResponse,
  WeeklyThroughputEntry,
} from "../api/types.js";
import { WaveFunnelTile } from "../components/WaveFunnelTile.js";
import { ResumeMetricsTile } from "../components/ResumeMetricsTile.js";
import { useDigestQuery } from "../hooks/useDigestQuery.js";
import { TrendSparkline } from "../components/TrendSparkline.js";

type ViewTab = "handoffs" | "ops" | "consultations" | "findings" | "dispatch-metrics" | "wave-funnel";

const VIEW_TABS: { id: ViewTab; label: string }[] = [
  { id: "handoffs", label: "Handoffs" },
  { id: "ops", label: "Ops Report" },
  { id: "consultations", label: "Consults" },
  { id: "findings", label: "Findings" },
  { id: "dispatch-metrics", label: "Dispatch Metrics" },
  { id: "wave-funnel", label: "Wave Funnel" },
];

function SubTabBar({
  active,
  onChange,
}: {
  active: ViewTab;
  onChange: (t: ViewTab) => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        gap: 0,
        borderBottom: "1px solid var(--border-soft)",
        padding: "0 16px",
        overflowX: "auto",
      }}
    >
      {VIEW_TABS.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          style={{
            background: "none",
            border: "none",
            borderBottom: active === t.id ? "2px solid var(--accent)" : "2px solid transparent",
            color: active === t.id ? "var(--accent)" : "var(--text-dim)",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            fontWeight: active === t.id ? 700 : 400,
            padding: "10px 14px",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function Spinner() {
  return (
    <div
      style={{
        padding: "40px 20px",
        textAlign: "center",
        color: "var(--text-dim)",
        fontFamily: "var(--font-mono)",
        fontSize: 11,
      }}
    >
      Loading...
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="quiet-state">
      <div className="glyph">◈</div>
      <h3>No data yet</h3>
      <p>{message}</p>
    </div>
  );
}

// The individual views are exported so the v4 Dispatch tab (PWA V2 slice 4)
// folds them in as sub-sections without duplicating any fetching or rendering.
export function HandoffsView({ onSelect }: { onSelect?: (item: Record<string, unknown>) => void } = {}) {
  const { data, loading } = useDigestQuery<SessionHandoffResponse>(getSessionHandoff);
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  if (loading) return <Spinner />;
  if (!data || data.handoffs.length === 0)
    return <EmptyState message="No session handoffs recorded yet." />;

  return (
    <>
      <div className="section-head">
        <h2>Session Handoffs</h2>
        <span className="count">{data.count}</span>
      </div>
      {data.handoffs.map((h, i) => (
        <div
          key={i}
          className="card"
          style={{ padding: "12px 14px", cursor: "pointer" }}
          onClick={() => { setExpandedIdx(expandedIdx === i ? null : i); onSelect?.(h as unknown as Record<string, unknown>); }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              marginBottom: 6,
            }}
          >
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                fontWeight: 700,
                color: "var(--text)",
              }}
            >
              {h.session_type ?? "session"} · {h.persona_id ?? "unknown"}
            </span>
            {h.closed_at && (
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  color: "var(--text-dim)",
                }}
              >
                {new Date(h.closed_at).toLocaleString()}
              </span>
            )}
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-dim)", marginLeft: "auto" }}>
              {expandedIdx === i ? "▾" : "▸"}
            </span>
          </div>
          {h.track_id && (
            <div
              style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-dim)", marginBottom: 4 }}
            >
              track: {h.track_id}
            </div>
          )}
          {h.steps_in_flight && h.steps_in_flight.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 4 }}>
              {h.steps_in_flight.map((stepId) => (
                <button
                  key={stepId}
                  onClick={() => window.__openEntity?.("steps", stepId)}
                  style={{
                    background: "none",
                    border: "1px solid var(--border-soft)",
                    borderRadius: 4,
                    padding: "2px 6px",
                    fontFamily: "var(--font-mono)",
                    fontSize: 9,
                    color: "var(--accent)",
                    cursor: "pointer",
                  }}
                >
                  {stepId}
                </button>
              ))}
            </div>
          )}
          {h.summary_text && (
            <div
              style={{
                fontSize: 11,
                color: "var(--text)",
                lineHeight: 1.5,
                marginBottom: 4,
                ...(expandedIdx !== i ? {
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                } : {}),
              }}
            >
              {h.summary_text}
            </div>
          )}
          {h.review_verdict && (
            <span
              className={`chip ${h.review_verdict === "GO" ? "ok" : h.review_verdict === "NO-GO" ? "danger" : "warn"}`}
              style={{ fontSize: 10, marginTop: 4 }}
            >
              {h.review_verdict}
            </span>
          )}
          {expandedIdx === i && (() => {
            const hh = h as unknown as Record<string, unknown>;
            return (
              <>
                {hh.work_summary != null && (
                  <div style={{ marginTop: 4, fontSize: 11, color: "var(--text)" }}>
                    <strong>Work summary:</strong> {String(hh.work_summary)}
                  </div>
                )}
                {Array.isArray(hh.key_decisions) && (hh.key_decisions as string[]).length > 0 && (
                  <div style={{ marginTop: 4, fontSize: 11 }}>
                    <strong>Key decisions:</strong>
                    {(hh.key_decisions as string[]).map((d: string, j: number) => (
                      <div key={j} style={{ paddingLeft: 8, color: "var(--text-muted)" }}>• {d}</div>
                    ))}
                  </div>
                )}
                {Array.isArray(hh.open_threads) && (hh.open_threads as string[]).length > 0 && (
                  <div style={{ marginTop: 4, fontSize: 11 }}>
                    <strong>Open threads:</strong>
                    {(hh.open_threads as string[]).map((t: string, j: number) => (
                      <div key={j} style={{ paddingLeft: 8, color: "var(--text-muted)" }}>• {t}</div>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      ))}
      <div style={{ height: 80 }} />
    </>
  );
}

export function OpsReportView() {
  const { data, loading } = useDigestQuery<OpsReportResponse>(getOpsReport);

  if (loading) return <Spinner />;
  if (!data) return <EmptyState message="Ops report unavailable." />;

  const { dispatches, events_by_type } = data;

  return (
    <>
      <div className="section-head">
        <h2>Dispatches (24h)</h2>
      </div>
      <div className="card" style={{ padding: "14px 16px" }}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
          {(
            [
              { label: "total", value: dispatches.total, color: "var(--text)" },
              { label: "complete", value: dispatches.complete, color: "var(--ok)" },
              { label: "failed", value: dispatches.failed, color: "var(--danger)" },
              { label: "running", value: dispatches.running, color: "var(--warn)" },
            ] as const
          ).map(({ label, value, color }) => (
            <div
              key={label}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                minWidth: 56,
              }}
            >
              <span
                style={{ fontFamily: "var(--font-mono)", fontSize: 20, fontWeight: 700, color }}
              >
                {value}
              </span>
              <span
                style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--text-dim)" }}
              >
                {label}
              </span>
            </div>
          ))}
        </div>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            color: "var(--text-dim)",
            borderTop: "1px solid var(--border-soft)",
            paddingTop: 8,
          }}
        >
          cost: ${dispatches.total_cost_usd.toFixed(4)}
        </div>
      </div>

      {events_by_type.length > 0 && (
        <>
          <div className="section-head">
            <h2>Events by Type (24h)</h2>
          </div>
          <div className="card" style={{ padding: "8px 14px" }}>
            {events_by_type.map((e, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "6px 0",
                  borderBottom:
                    i < events_by_type.length - 1 ? "1px solid var(--border-soft)" : "none",
                }}
              >
                <span
                  style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text)" }}
                >
                  {e.event_type}
                </span>
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 10,
                    fontWeight: 700,
                    color: "var(--text-dim)",
                  }}
                >
                  {e.count}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
      <div style={{ height: 80 }} />
    </>
  );
}

export function ConsultationsView({ onSelect }: { onSelect?: (item: Record<string, unknown>) => void } = {}) {
  const { data, loading } = useDigestQuery<ConsultationSummaryResponse>(getConsultationSummary);
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  if (loading) return <Spinner />;
  if (!data || data.total === 0) return <EmptyState message="No consultations recorded yet." />;

  return (
    <>
      <div className="section-head">
        <h2>By Persona</h2>
        <span className="count">{data.total}</span>
      </div>
      <div className="card" style={{ padding: "8px 14px" }}>
        {data.by_persona.map((p, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "6px 0",
              borderBottom:
                i < data.by_persona.length - 1 ? "1px solid var(--border-soft)" : "none",
            }}
          >
            <span
              style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text)" }}
            >
              {p.consulted_persona}
            </span>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                fontWeight: 700,
                color: "var(--text-dim)",
              }}
            >
              {p.count}
            </span>
          </div>
        ))}
      </div>

      {data.recent.length > 0 && (
        <>
          <div className="section-head">
            <h2>Recent</h2>
          </div>
          {data.recent.map((r, i) => (
            <div key={i} className="card" style={{ padding: "10px 14px", cursor: "pointer" }}
              onClick={() => { setExpandedIdx(expandedIdx === i ? null : i); onSelect?.(r as Record<string, unknown>); }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  marginBottom: 4,
                }}
              >
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 10,
                    fontWeight: 700,
                    color: "var(--text)",
                  }}
                >
                  {String(r.consulted_persona ?? "")}
                </span>
                {r.created_at != null && (
                  <span
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 10,
                      color: "var(--text-dim)",
                    }}
                  >
                    {new Date(String(r.created_at)).toLocaleString()}
                  </span>
                )}
              </div>
              {r.step_id != null && (
                <button
                  onClick={(e) => { e.stopPropagation(); window.__openEntity?.("steps", String(r.step_id)); }}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    fontFamily: "var(--font-mono)",
                    fontSize: 10,
                    color: "var(--accent)",
                    cursor: "pointer",
                    marginBottom: 2,
                    display: "block",
                    textAlign: "left",
                  }}
                >
                  step: {String(r.step_id)}
                </button>
              )}
              {r.input_summary != null && (
                <div
                  style={{
                    fontSize: 10,
                    color: "var(--text)",
                    ...(expandedIdx !== i ? {
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    } : {}),
                  }}
                >
                  {String(r.input_summary)}
                </div>
              )}
              {expandedIdx === i && (
                <>
                  {(r as Record<string, unknown>).outcome != null && (
                    <div style={{ marginTop: 4, fontSize: 11 }}>
                      <strong>Outcome:</strong> {String((r as Record<string, unknown>).outcome)}
                    </div>
                  )}
                  {(r as Record<string, unknown>).output_summary != null && (
                    <div style={{ marginTop: 4, fontSize: 11, color: "var(--text-muted)" }}>
                      <strong>Output:</strong> {String((r as Record<string, unknown>).output_summary)}
                    </div>
                  )}
                  {(r as Record<string, unknown>).notes != null && (
                    <div style={{ marginTop: 4, fontSize: 11, color: "var(--text-muted)" }}>
                      <strong>Notes:</strong> {String((r as Record<string, unknown>).notes)}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </>
      )}
      <div style={{ height: 80 }} />
    </>
  );
}

export function FindingsView() {
  const { data, loading } = useDigestQuery<DiagnosticReportResponse>(getDiagnosticReport);

  if (loading) return <Spinner />;
  if (!data) return <EmptyState message="Diagnostic report unavailable." />;

  const statusEntries = Object.entries(data.counts_by_status).sort((a, b) => b[1] - a[1]);
  const typeEntries = Object.entries(data.counts_by_type).sort((a, b) => b[1] - a[1]);

  return (
    <>
      {data.latest_run && (
        <>
          <div className="section-head">
            <h2>Latest Run</h2>
          </div>
          <div className="card" style={{ padding: "12px 14px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
              }}
            >
              <span
                style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text)" }}
              >
                {String(data.latest_run.run_id)}
              </span>
              {data.latest_run.status && (
                <span
                  className={`chip ${
                    data.latest_run.status === "complete"
                      ? "ok"
                      : data.latest_run.status === "failed"
                        ? "danger"
                        : ""
                  }`}
                  style={{ fontSize: 10 }}
                >
                  {String(data.latest_run.status)}
                </span>
              )}
            </div>
            {data.latest_run.started_at && (
              <div
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  color: "var(--text-dim)",
                  marginTop: 4,
                }}
              >
                {new Date(data.latest_run.started_at).toLocaleString()}
              </div>
            )}
            {data.latest_run.trigger && (
              <div
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  color: "var(--text-dim)",
                  marginTop: 2,
                }}
              >
                trigger: {String(data.latest_run.trigger)}
              </div>
            )}
          </div>
        </>
      )}

      {statusEntries.length > 0 && (
        <>
          <div className="section-head">
            <h2>By Status</h2>
          </div>
          <div style={{ display: "flex", gap: 8, padding: "0 20px 4px", flexWrap: "wrap" }}>
            {statusEntries.map(([status, count]) => (
              <span
                key={status}
                className={`chip ${status === "open" ? "warn" : status === "resolved" ? "ok" : ""}`}
                style={{ fontSize: 11 }}
              >
                {status} · {count}
              </span>
            ))}
          </div>
        </>
      )}

      {typeEntries.length > 0 && (
        <>
          <div className="section-head">
            <h2>By Type</h2>
          </div>
          <div style={{ display: "flex", gap: 8, padding: "0 20px 4px", flexWrap: "wrap" }}>
            {typeEntries.map(([type, count]) => (
              <span key={type} className="chip" style={{ fontSize: 11 }}>
                {type} · {count}
              </span>
            ))}
          </div>
        </>
      )}

      <div className="section-head">
        <h2>Findings</h2>
        <span className="count">{data.findings.length}</span>
      </div>
      {data.findings.length === 0 ? (
        <div className="quiet-state">
          <div className="glyph">✓</div>
          <h3>No recent findings</h3>
          <p>All clear in the last 50 results.</p>
        </div>
      ) : null}
      {data.findings.map((f, i) => (
        <div key={i} className="card" style={{ padding: "10px 14px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 8,
              marginBottom: 4,
            }}
          >
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                color: "var(--text)",
                fontWeight: 600,
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {f.title}
            </span>
            <span
              className={`chip ${f.status === "open" ? "warn" : f.status === "resolved" ? "ok" : ""}`}
              style={{ fontSize: 9, flexShrink: 0 }}
            >
              {f.status}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              gap: 10,
              fontFamily: "var(--font-mono)",
              fontSize: 9,
              color: "var(--text-dim)",
            }}
          >
            {f.finding_type && <span>{f.finding_type}</span>}
            {f.recurrence_count && f.recurrence_count > 1 && (
              <span style={{ color: "var(--warn)" }}>×{f.recurrence_count}</span>
            )}
            {f.last_seen_at && (
              <span>{new Date(f.last_seen_at).toLocaleDateString()}</span>
            )}
          </div>
          {f.description && (
            <div
              style={{
                fontSize: 10,
                color: "var(--text)",
                marginTop: 4,
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {f.description}
            </div>
          )}
        </div>
      ))}
      <div style={{ height: 80 }} />
    </>
  );
}

function FrictionFunnelSection() {
  const { data, loading } = useDigestQuery<FrictionFunnelMetrics>(fetchFrictionFunnel);

  if (loading) return <Spinner />;
  if (!data) return <EmptyState message="Friction funnel data unavailable." />;

  type MetricDef = {
    key: string;
    label: string;
    value: string;
    unit: string;
    alert: boolean;
    alertLabel?: string;
  };

  const metrics: MetricDef[] = [
    {
      key: "proposals_7d",
      label: "Friction Proposals (7d)",
      value: `${data.proposals_7d}`,
      unit: "",
      alert: false,
    },
    {
      key: "vlr_created",
      label: "VLRs Created",
      value: `${data.vlr_created}`,
      unit: "",
      alert: false,
    },
    {
      key: "skip_count",
      label: "Deduped / Skipped",
      value: `${data.skip_count}`,
      unit: "",
      alert: false,
    },
    {
      key: "skip_rate",
      label: "Skip Rate",
      value: data.skip_rate != null ? `${data.skip_rate}` : "—",
      unit: "%",
      alert: data.skip_rate != null && data.skip_rate > 70,
      alertLabel: "> 70% threshold",
    },
  ];

  return (
    <>
      <div className="section-head" style={{ marginTop: 12 }}>
        <h2>Friction Funnel (7d)</h2>
        <span className="count">{data.proposals_7d}</span>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
          gap: 8,
          padding: "0 16px",
        }}
      >
        {metrics.map((m) => (
          <div
            key={m.key}
            className="card"
            style={{
              padding: "12px 14px",
              borderLeft: m.alert ? "3px solid var(--danger)" : "3px solid transparent",
              background: m.alert ? "color-mix(in srgb, var(--danger) 8%, var(--surface))" : undefined,
            }}
          >
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 9,
                color: m.alert ? "var(--danger)" : "var(--text-dim)",
                marginBottom: 4,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              {m.label}
            </div>
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 22,
                fontWeight: 700,
                color: m.alert ? "var(--danger)" : "var(--text)",
                lineHeight: 1.1,
              }}
            >
              {m.value}
              {m.unit && (
                <span style={{ fontSize: 11, fontWeight: 400, marginLeft: 2, color: "var(--text-dim)" }}>
                  {m.unit}
                </span>
              )}
            </div>
            {m.alert && m.alertLabel && (
              <div
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 9,
                  color: "var(--danger)",
                  marginTop: 4,
                }}
              >
                ⚠ {m.alertLabel}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

function WaveFunnelView() {
  const { data, loading } = useDigestQuery<WaveFunnelResponse>(getWaveFunnel);

  if (loading) return <Spinner />;
  if (!data) return <EmptyState message="Wave funnel data unavailable." />;

  return (
    <>
      <div className="section-head">
        <h2>Wave Funnel (14d)</h2>
        <span className="count">{data.wave_funnel.length}</span>
      </div>
      <WaveFunnelTile data={data.wave_funnel} />
    </>
  );
}

function fetchDispatchMetrics(): Promise<DispatchMetricsResponse> {
  return getDispatchMetrics({ include: ['429_resumes', 'weekly_throughput'] });
}

const THROUGHPUT_COLS = ["Week", "PRs", "PR/hr", "Steps", "Step/hr", "Steps/PR"] as const;

function WeeklyThroughputTable({ rows }: { rows: WeeklyThroughputEntry[] }) {
  const sorted = [...rows].sort(
    (a, b) => new Date(b.week_start).getTime() - new Date(a.week_start).getTime(),
  );

  return (
    <>
      <div className="section-head" style={{ marginTop: 12 }}>
        <h2>Week-over-Week Throughput</h2>
        <span className="count">{sorted.length}w</span>
      </div>
      <div
        style={{
          overflowX: "auto",
          padding: "0 16px",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontFamily: "var(--font-mono)",
            fontSize: 10,
          }}
        >
          <thead>
            <tr>
              {THROUGHPUT_COLS.map((col) => (
                <th
                  key={col}
                  style={{
                    textAlign: col === "Week" ? "left" : "right",
                    padding: "6px 8px",
                    borderBottom: "2px solid var(--border-soft)",
                    color: "var(--text-dim)",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    fontSize: 9,
                    whiteSpace: "nowrap",
                  }}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r.label}>
                <td
                  style={{
                    padding: "6px 8px",
                    borderBottom: "1px solid var(--border-soft)",
                    color: "var(--text)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {r.label}
                  {r.is_partial && (
                    <span
                      style={{
                        marginLeft: 4,
                        fontSize: 8,
                        color: "var(--text-dim)",
                        fontStyle: "italic",
                      }}
                    >
                      partial ({r.hours_in_window}h)
                    </span>
                  )}
                </td>
                <TdNum value={r.prs_merged} />
                <TdNum value={r.pr_per_hr} decimals={3} />
                <TdNum value={r.steps_merged} />
                <TdNum value={r.step_per_hr} decimals={3} />
                <TdNum value={r.steps_per_pr} decimals={2} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function TdNum({ value, decimals = 0 }: { value: number | null; decimals?: number }) {
  return (
    <td
      style={{
        padding: "6px 8px",
        borderBottom: "1px solid var(--border-soft)",
        color: value == null ? "var(--text-dim)" : "var(--text)",
        textAlign: "right",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {value == null ? "—" : decimals > 0 ? value.toFixed(decimals) : value}
    </td>
  );
}

export function DispatchMetricsView() {
  const { data, loading } = useDigestQuery<DispatchMetricsResponse>(fetchDispatchMetrics);

  if (loading) return <Spinner />;
  if (!data) return <EmptyState message="Dispatch metrics unavailable." />;

  type MetricDef = {
    key: string;
    label: string;
    value: string;
    unit: string;
    alert: boolean;
    alertLabel?: string;
  };

  const metrics: MetricDef[] = [
    {
      key: "success_rate",
      label: "Dispatch Success Rate (24h)",
      value: data.dispatch_success_rate_24h != null ? `${data.dispatch_success_rate_24h}` : "—",
      unit: "%",
      alert: false,
    },
    {
      key: "productive_turn",
      label: "Avg Productive Turn %",
      value: data.avg_productive_turn_pct != null ? `${data.avg_productive_turn_pct}` : "—",
      unit: "%",
      alert: false,
    },
    {
      key: "friction_turn",
      label: "Avg Friction Turn %",
      value: data.avg_friction_turn_pct != null ? `${data.avg_friction_turn_pct}` : "—",
      unit: "%",
      alert: data.avg_friction_turn_pct != null && data.avg_friction_turn_pct > 40,
      alertLabel: "> 40% threshold",
    },
    {
      key: "top_friction",
      label: "Top Friction Source (7d)",
      value: data.top_friction_source ?? "—",
      unit: "",
      alert: false,
    },
    {
      key: "permission_denied",
      label: "Permission Denied Rate",
      value: data.permission_denied_rate != null ? `${data.permission_denied_rate}` : "—",
      unit: "/dispatch",
      alert: data.permission_denied_rate != null && data.permission_denied_rate > 5,
      alertLabel: "> 5/dispatch threshold",
    },
    {
      key: "mailbox_adoption",
      label: "Mailbox Adoption Rate",
      value: data.mailbox_adoption_rate != null ? `${data.mailbox_adoption_rate}` : "—",
      unit: "%",
      alert: false,
    },
    {
      key: "premature_termination",
      label: "Premature Termination Rate",
      value: data.premature_termination_rate != null ? `${data.premature_termination_rate}` : "—",
      unit: "%",
      alert: data.premature_termination_rate != null && data.premature_termination_rate > 10,
      alertLabel: "> 10% threshold",
    },
    {
      key: "cost_per_pr",
      label: "Cost per Merged PR (30d)",
      value: data.cost_per_merged_pr != null ? `$${data.cost_per_merged_pr.toFixed(2)}` : "—",
      unit: "",
      alert: false,
    },
    {
      key: "callback_lost",
      label: "Callback Lost Rate (7d)",
      value: data.callback_lost_rate != null ? `${data.callback_lost_rate}` : "—",
      unit: "%",
      alert: false,
    },
    {
      key: "status_mismatch",
      label: "Status Mismatch Count",
      value: `${data.status_mismatch_count}`,
      unit: "",
      alert: false,
    },
    {
      key: "avg_startup",
      label: "Avg Startup Turns",
      value: data.avg_startup_turns != null ? `${data.avg_startup_turns}` : "—",
      unit: "turns",
      alert: false,
    },
    {
      key: "orphan_droplet",
      label: "Orphan Droplet Count",
      value: `${data.orphan_droplet_count}`,
      unit: "",
      alert: data.orphan_droplet_count > 0,
      alertLabel: "orphan detected",
    },
  ];

  return (
    <>
      <div className="section-head">
        <h2>Dispatch Operations Metrics</h2>
        <span className="count">{metrics.length}</span>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
          gap: 8,
          padding: "0 16px",
        }}
      >
        {metrics.map((m) => (
          <div
            key={m.key}
            className="card"
            style={{
              padding: "12px 14px",
              borderLeft: m.alert ? "3px solid var(--danger)" : "3px solid transparent",
              background: m.alert ? "color-mix(in srgb, var(--danger) 8%, var(--surface))" : undefined,
            }}
          >
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 9,
                color: m.alert ? "var(--danger)" : "var(--text-dim)",
                marginBottom: 4,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              {m.label}
            </div>
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 22,
                fontWeight: 700,
                color: m.alert ? "var(--danger)" : "var(--text)",
                lineHeight: 1.1,
              }}
            >
              {m.value}
              {m.unit && (
                <span style={{ fontSize: 11, fontWeight: 400, marginLeft: 2, color: "var(--text-dim)" }}>
                  {m.unit}
                </span>
              )}
            </div>
            {m.alert && m.alertLabel && (
              <div
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 9,
                  color: "var(--danger)",
                  marginTop: 4,
                }}
              >
                ⚠ {m.alertLabel}
              </div>
            )}
            <div style={{ marginTop: 6 }}>
              <TrendSparkline
                series={data.trends?.[m.key] ?? null}
                height={24}
                color={m.alert ? "var(--danger)" : "var(--text-dim)"}
              />
            </div>
          </div>
        ))}
      </div>

      {data.callback_lost_by_persona.length > 0 && (
        <>
          <div className="section-head" style={{ marginTop: 12 }}>
            <h2>Callback Lost by Persona (7d)</h2>
          </div>
          <div className="card" style={{ padding: "8px 14px" }}>
            {data.callback_lost_by_persona.map((p, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "6px 0",
                  borderBottom:
                    i < data.callback_lost_by_persona.length - 1
                      ? "1px solid var(--border-soft)"
                      : "none",
                }}
              >
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text)" }}>
                  {p.persona_id}
                </span>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-dim)" }}>
                    ×{p.count}
                  </span>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 700, color: "var(--warn)" }}>
                    {p.rate}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <FrictionFunnelSection />

      {data["429_resumes"] && (
        <>
          <div className="section-head" style={{ marginTop: 12 }}>
            <h2>429 Resume Metrics (7d)</h2>
          </div>
          <ResumeMetricsTile data={data["429_resumes"]} />
        </>
      )}

      {data.weekly_throughput && data.weekly_throughput.length > 0 && (
        <WeeklyThroughputTable rows={data.weekly_throughput} />
      )}

      <div style={{ height: 80 }} />
    </>
  );
}

export function OperationalViewsScreen() {
  const [activeView, setActiveView] = useState<ViewTab>("handoffs");

  function renderView() {
    switch (activeView) {
      case "handoffs":
        return <HandoffsView />;
      case "ops":
        return <OpsReportView />;
      case "consultations":
        return <ConsultationsView />;
      case "findings":
        return <FindingsView />;
      case "dispatch-metrics":
        return <DispatchMetricsView />;
      case "wave-funnel":
        return <WaveFunnelView />;
    }
  }

  return (
    <>
      <SubTabBar active={activeView} onChange={setActiveView} />
      {renderView()}
    </>
  );
}
